import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import ProfileSidebar from '../components/profile/ProfileSidebar';
import PacientePlanillaForm from '../components/profile/PacientePlanillaForm';
import {
  emptyPatientForm,
  getPatientLinkLabel,
  mapPatientFromApi,
  mapPatientsToPayload,
} from '../constants/childInterests';
import { getAgendaCountry } from '../constants/agendaTherapyTypes';
import { pacienteDesdeValores, valoresDesdePaciente } from '../constants/pacientePlanilla';
import {
  getUserSession,
  mergeSessionUser,
  saveUserSession,
} from '../services/authService';
import {
  fetchUserProfile,
  getChildAgeLabel,
  getChildGenderLabel,
  saveUserChildren,
} from '../services/profileService';
import { getPersonNameError } from '../utils/personName';
import '../styles/profile.css';

const UserFieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.5-7 8-7s8 3 8 7" />
  </svg>
);

const genderClass = (genero) => {
  if (genero === 'nina' || genero === 'mujer') {
    return 'nina';
  }

  return 'nino';
};

const getCountryLabel = (paisId) => {
  const match = getAgendaCountry(paisId);
  return match ? match.label : '';
};

/** Solo filas de fichas ya guardadas (nombre + cédula); evita mezclar con el botón +. */
const isCreatedPatient = (patient) =>
  Boolean(
    patient &&
      String(patient.nombre || '').trim() &&
      String(patient.documentoNumero || '').trim()
  );

const ProfilePatientsPage = () => {
  const sessionUser = getUserSession();
  const sessionUserId = sessionUser ? sessionUser.id : null;
  const [profile, setProfile] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyPatientForm());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadWarning, setLoadWarning] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!sessionUserId) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    fetchUserProfile(sessionUserId)
      .then((data) => {
        if (cancelled) {
          return;
        }

        setProfile(data);
        setLoadWarning('');
        const list = Array.isArray(data.hijos) ? data.hijos : data.pacientes || [];
        setPacientes(list.map(mapPatientFromApi));
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadWarning(error.message || 'No se pudo cargar la información de pacientes.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionUserId]);

  if (!sessionUser) {
    return <Redirect to="/iniciar-sesion" />;
  }

  const profileIncomplete = profile && profile.perfil_completo === false;
  const missingPatients =
    profile &&
    ((profile.campos_faltantes || []).includes('pacientes') ||
      (profile.campos_faltantes || []).includes('hijos'));
  const isEditing = editingIndex !== null;

  const persistPatients = (nextPatients, successMessage) => {
    setIsSaving(true);
    setErrorMessage('');
    setMessage('');

    return saveUserChildren(sessionUserId, mapPatientsToPayload(false, nextPatients))
      .then((result) => {
        const saved = (result.hijos || result.pacientes || []).map(mapPatientFromApi);
        setPacientes(saved);
        setProfile((current) =>
          current
            ? {
                ...current,
                sin_hijos: result.sin_hijos,
                total_hijos: result.total_hijos || result.total_pacientes || saved.length,
                hijos: result.hijos || result.pacientes,
                perfil_completo: result.perfil_completo,
                campos_faltantes: result.campos_faltantes || [],
              }
            : current
        );

        const updatedSession = mergeSessionUser({
          perfil_completo: result.perfil_completo,
          campos_faltantes: result.campos_faltantes || [],
        });

        if (updatedSession) {
          saveUserSession(updatedSession);
        }

        setMessage(successMessage || 'Cambios guardados correctamente.');
        setEditingIndex(null);
        setDraft(emptyPatientForm());
      })
      .catch((error) => {
        setErrorMessage(error.message || 'No se pudo guardar la ficha del paciente.');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const startEditing = (index) => {
    const current = pacientes[index];
    setEditingIndex(index);
    setDraft({
      ...current,
      planillaValores: valoresDesdePaciente(current),
    });
    setErrorMessage('');
    setMessage('');
  };

  const startAdding = () => {
    const empty = emptyPatientForm();
    setEditingIndex(pacientes.length);
    setDraft({
      ...empty,
      planillaValores: valoresDesdePaciente(empty),
    });
    setErrorMessage('');
    setMessage('');
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setDraft(emptyPatientForm());
    setErrorMessage('');
  };

  const validateDraft = () => {
    if (!draft.nombre || !draft.nombre.trim()) {
      return 'Indica el nombre completo del paciente.';
    }

    const nameError = getPersonNameError(draft.nombre);
    if (nameError) {
      return nameError;
    }

    if (!draft.documentoNumero || !draft.documentoNumero.trim()) {
      return 'La cédula del paciente es obligatoria.';
    }

    if (!draft.pais) {
      return 'Selecciona el país del paciente.';
    }

    if (!draft.fechaNacimiento) {
      return 'Indica la fecha de nacimiento.';
    }

    if (['nino', 'nina', 'hombre', 'mujer'].indexOf(draft.genero) === -1) {
      return 'Selecciona el género del paciente.';
    }

    if (!draft.vinculo) {
      return 'Selecciona el vínculo con el paciente.';
    }

    return '';
  };

  const handleSaveDraft = () => {
    const validationError = validateDraft();

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const nextPatients = [...pacientes];

    if (editingIndex !== null && editingIndex < nextPatients.length) {
      nextPatients[editingIndex] = { ...draft };
    } else {
      nextPatients.push({ ...draft });
    }

    persistPatients(nextPatients, 'Paciente guardado correctamente.');
  };

  if (sessionUser && sessionUser.tipo !== 'paciente') {
    return <Redirect to="/perfil" />;
  }

  return (
    <div className="profile-root">
      <div className="profile-layout">
        <ProfileSidebar activeId="pacientes" />

        <section className="profile-main">
          {isLoading ? <p className="profile-status">Cargando pacientes...</p> : null}

          {!isLoading ? (
            <>
              <div className="profile-review-header">
                <div>
                  <h1 className="profile-review-title">Pacientes</h1>
                  <p className="profile-review-intro">
                    Completa la planilla del paciente por pestañas. Los datos de identificación
                    siguen siendo obligatorios; el resto se puede ir llenando después.
                  </p>
                </div>
              </div>

              {loadWarning ? (
                <p className="profile-status profile-status--warning">{loadWarning}</p>
              ) : null}

              {profileIncomplete && missingPatients ? (
                <div className="profile-pending-banner" role="status">
                  <strong>Completa tu perfil.</strong> Agrega al menos un paciente con nombre,
                  cédula y país.
                </div>
              ) : null}

              <div className="profile-children-list">
                {pacientes.map((patient, index) => {
                  if (!isCreatedPatient(patient)) {
                    return null;
                  }

                  const countryLabel = getCountryLabel(patient.pais);

                  return (
                    <button
                      key={`patient-row-${patient.id || index}`}
                      type="button"
                      className="profile-child-row"
                      onClick={() => startEditing(index)}
                      disabled={isEditing || isSaving}
                    >
                      <span
                        className={`profile-child-gender profile-child-gender--${genderClass(
                          patient.genero
                        )}`}
                      >
                        Paciente
                      </span>
                      <span className="profile-child-age">
                        <span className="profile-child-age-icon">
                          <UserFieldIcon />
                        </span>
                        <span className="profile-patient-summary">
                          <strong>{patient.nombre}</strong>
                          <span>
                            Cédula {patient.documentoNumero}
                            {countryLabel ? ` · ${countryLabel}` : ''}
                            {' · '}
                            {getChildGenderLabel(patient.genero)} ·{' '}
                            {getChildAgeLabel(patient.fechaNacimiento)} ·{' '}
                            {getPatientLinkLabel(patient.vinculo)}
                          </span>
                        </span>
                      </span>
                    </button>
                  );
                })}

                {!pacientes.some(isCreatedPatient) ? (
                  <p className="profile-review-intro">
                    Aún no hay pacientes. Usa el botón + para crear el primero.
                  </p>
                ) : null}

                <button
                  type="button"
                  className="profile-child-add"
                  onClick={startAdding}
                  aria-label="Agregar paciente"
                >
                  +
                </button>
              </div>

              {isEditing ? (
                <div className="profile-child-editor">
                  <p className="profile-child-editor-title">
                    {editingIndex !== null && editingIndex < pacientes.length
                      ? 'Editar paciente'
                      : 'Agregar paciente'}
                  </p>

                  <PacientePlanillaForm
                    paciente={draft}
                    valores={draft.planillaValores}
                    onChange={(valores) => {
                      setDraft((current) => pacienteDesdeValores(current, valores));
                    }}
                  />

                  {errorMessage ? (
                    <p className="profile-status profile-status--error">{errorMessage}</p>
                  ) : null}

                  <div className="profile-child-editor-actions">
                    <button
                      type="button"
                      className="profile-edit-save"
                      onClick={handleSaveDraft}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button
                      type="button"
                      className="profile-edit-cancel"
                      onClick={cancelEditing}
                      disabled={isSaving}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}

              {message ? <p className="profile-status">{message}</p> : null}
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default ProfilePatientsPage;
